import React from "react";
import { PropTypes } from "prop-types";
import { observer } from "mobx-react";
import { MrbButton } from "mrb/components/button";
import { MrbSelect } from "mrb/components/select";
import { MrbNumericInput } from "mrb/components/input";
import { MrbCheckbox } from "mrb/components/checkbox";
import { isFunction } from "lodash";

function NakiNigunAlbumTrackProducerEditTemplate({ nakiNigunAlbumTrackProducerEditViewStore, closeModal, t }) {
    const {
        producerRows,
        totalRevenue,
        isValid,
        isSaving,
        addRow,
        removeRow,
        setRowRevenue,
        save,
        trackTitle,
        heading,
        allowKeepExisting,
        keepExisting,
        setKeepExisting,
    } = nakiNigunAlbumTrackProducerEditViewStore;

    return (
        <React.Fragment>
            <p className="u-type--base u-type--wgt--bold u-mar--bottom--med">
                {/* Consumers (e.g. the music-import editor) can supply a full contextual heading;
                    the songs editor leaves it unset and keeps the default "Edit Track Producers - <title>". */}
                {heading || `${t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.TITLE")} - ${trackTitle}`}
            </p>

            {producerRows.map((row, index) => (
                <ProducerRowItem
                    key={index}
                    row={row}
                    index={index}
                    removeRow={removeRow}
                    setRowRevenue={setRowRevenue}
                    t={t}
                />
            ))}

            <div className="u-mar--bottom--med">
                <MrbButton
                    classNameOverride="c-btn c-btn--sml c-btn--primary"
                    label={t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.ADD_PRODUCER")}
                    onClick={addRow}
                />
            </div>

            {producerRows.length > 0 && (
                <div className="u-mar--bottom--med">
                    <p
                        className={`u-type--sml ${
                            Math.round(totalRevenue * 100) / 100 === 100
                                ? "u-type--color--success"
                                : "u-type--color--warning"
                        }`}
                    >
                        {t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.TOTAL_REVENUE")}:{" "}
                        {Math.round(totalRevenue * 100) / 100}%
                        {Math.round(totalRevenue * 100) / 100 !== 100 && (
                            <span className="u-mar--left--sml">
                                ({t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.VALIDATION.REVENUE_MUST_EQUAL_100")})
                            </span>
                        )}
                    </p>
                </div>
            )}

            {allowKeepExisting && (
                <div className="u-mar--bottom--med">
                    <MrbCheckbox
                        id="keep-existing-producers"
                        label="ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.PRODUCER_EDITOR.KEEP_EXISTING"
                        value={!!keepExisting}
                        onChange={() => setKeepExisting(!keepExisting)}
                        t={t}
                    />
                </div>
            )}

            <div className="u-mar--top--med u-display--flex u-display--flex--jc--flex-end u-gap--sml">
                {closeModal && isFunction(closeModal) && (
                    <MrbButton
                        t={t}
                        className="c-btn c-btn--base c-btn--ghost"
                        label={t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.BUTTONS.CANCEL")}
                        onClick={closeModal}
                    />
                )}
                <MrbButton
                    t={t}
                    className="c-btn c-btn--base c-btn--primary"
                    label={t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.BUTTONS.SAVE")}
                    onClick={save}
                    disabled={!isValid || isSaving}
                    aria-busy={isSaving}
                />
            </div>
        </React.Fragment>
    );
}

const ProducerRowItem = observer(function ProducerRowItem({ row, index, removeRow, setRowRevenue, t }) {
    return (
        <div className="row row--align-end u-mar--bottom--sml">
            <div className="col col-sml-12 col-lrg-7 u-mar--bottom--sml">
                <label className="c-form__field__label">
                    {t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.PRODUCER_LABEL")}
                </label>
                <MrbSelect store={row.producerSelectStore} classNameExtend="c-dropdown--dark" />
            </div>
            <div className="col col-sml-12 col-lrg-3 u-mar--bottom--sml">
                <label className="c-form__field__label">
                    {t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.REVENUE_LABEL")}
                </label>
                {row.revenuePercentage < 0.01 && (
                    <p className="u-type--sml u-type--color--warning">
                        {t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.VALIDATION.REVENUE_MIN")}
                    </p>
                )}
                <MrbNumericInput
                    className="c-input c-input--outline c-input--numeric c-input--numeric--base"
                    precision={2}
                    step={0.01}
                    t={t}
                    value={row.revenuePercentage}
                    min={0}
                    max={100}
                    onChange={(e) => setRowRevenue(index, e)}
                    strict={true}
                />
            </div>
            <div className="col col-sml-12 col-lrg-2 u-mar--bottom--sml">
                <div className="u-display--flex u-display--flex--ai--center" style={{ height: "38px" }}>
                    <MrbButton
                        classNameOverride="c-btn c-btn--icon"
                        onlyIcon
                        icon="u-icon u-icon--med u-icon--trash"
                        onClick={() => removeRow(index)}
                    />
                </div>
            </div>
        </div>
    );
});

NakiNigunAlbumTrackProducerEditTemplate.propTypes = {
    nakiNigunAlbumTrackProducerEditViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

export default observer(NakiNigunAlbumTrackProducerEditTemplate);
